import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import {
  CompletePickupPayload,
  CompletePickupResponse,
  PickupRequestODataResponse,
} from '../entities/pickup-request.model';

/**
 * Projeção de `GET /OData/v2/AccessPoints` recortada pelo `$select` abaixo —
 * o endpoint devolve array nu, sem envelope `{ value }`.
 */
interface AccessPointEntity {
  AccessPointId: string;
  Name: string;
}

@Injectable({ providedIn: 'root' })
export class LivePickupService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(APP_CONFIG);

  public getOpenPickups(
    accessPointId: string,
  ): Observable<PickupRequestODataResponse> {
    return this.http.get<PickupRequestODataResponse>(
      `${this.config.apiUrl.odata_access_flow_service}/PickupRequests`,
      {
        params: {
          $filter: `AccessPointId eq ${accessPointId} and (Status eq 0 or Status eq 1)`,
          $orderby: 'RequestedOnUtc asc',
        },
        withCredentials: true,
      },
    );
  }

  public getRecentCompleted(
    accessPointId: string,
    top = 5,
  ): Observable<PickupRequestODataResponse> {
    return this.http.get<PickupRequestODataResponse>(
      `${this.config.apiUrl.odata_access_flow_service}/PickupRequests`,
      {
        params: {
          $filter: `AccessPointId eq ${accessPointId} and Status eq 2`,
          $orderby: 'CompletedOnUtc desc',
          $top: top,
        },
        withCredentials: true,
      },
    );
  }

  public getAllOpenPickups(): Observable<PickupRequestODataResponse> {
    return this.http.get<PickupRequestODataResponse>(
      `${this.config.apiUrl.odata_access_flow_service}/PickupRequests`,
      {
        params: {
          $filter: 'Status eq 0 or Status eq 1',
          $orderby: 'RequestedOnUtc asc',
        },
        withCredentials: true,
      },
    );
  }

  public getLatestPickupForHero(
    accessPointId?: string | null,
  ): Observable<PickupRequestODataResponse> {
    const statusFilter = '(Status eq 0 or Status eq 1)';
    return this.http.get<PickupRequestODataResponse>(
      `${this.config.apiUrl.odata_access_flow_service}/PickupRequests`,
      {
        params: {
          $filter: accessPointId
            ? `AccessPointId eq ${accessPointId} and ${statusFilter}`
            : statusFilter,
          $orderby: 'RequestedOnUtc desc,StudentDisplayName asc',
          $top: 1,
        },
        withCredentials: true,
      },
    );
  }

  public getAccessPoints(): Observable<AccessPointEntity[]> {
    return this.http.get<AccessPointEntity[]>(
      `${this.config.apiUrl.access_directory_service}/OData/v2/AccessPoints`,
      {
        params: {
          $orderby: 'Name',
          $select: 'AccessPointId,Name',
        },
        withCredentials: true,
      },
    );
  }

  public getOpenPickupsCount(): Observable<PickupRequestODataResponse> {
    return this.http.get<PickupRequestODataResponse>(
      `${this.config.apiUrl.odata_access_flow_service}/PickupRequests`,
      {
        params: {
          $filter: '(Status eq 0) or (Status eq 1)',
          $count: true,
          $top: 0,
        },
        withCredentials: true,
      },
    );
  }

  public getAllRecentCompleted(
    top = 5,
  ): Observable<PickupRequestODataResponse> {
    return this.http.get<PickupRequestODataResponse>(
      `${this.config.apiUrl.odata_access_flow_service}/PickupRequests`,
      {
        params: {
          $filter: 'Status eq 2',
          $orderby: 'CompletedOnUtc desc',
          $top: top,
        },
        withCredentials: true,
      },
    );
  }

  public completePickup(
    pickupId: string,
    payload?: CompletePickupPayload,
  ): Observable<CompletePickupResponse> {
    return this.http.post<CompletePickupResponse>(
      `${this.config.apiUrl.access_flow_service}/v1/Pickups/${pickupId}/complete`,
      payload ?? {},
      { withCredentials: true },
    );
  }
}
